/**
 * OperatorRouteView - Detailed route view for single operator
 * Shows: Map with stores, Timeline, and for each store: campaigns/formats delivered + photos
 */

import React, { useMemo, useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useParams } from "wouter";
import { TrackerLayout } from "./TrackerLayout";
import { TaskSubmissionNotes } from "./TaskSubmissionNotes";
import { getJson } from "@/lib/http";
import { copyTaskDefinitionIdsToParams, trackerApiQueryString } from "@/lib/trackerTaskDefinitionFilter";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { formatDistanceToNow, format } from "date-fns";
import {
    MapContainer,
    Polyline,
    TileLayer,
    useMap,
    CircleMarker,
    Popup
} from "react-leaflet";
import L from "leaflet";
import markerIcon2x from "leaflet/dist/images/marker-icon-2x.png";
import markerIcon from "leaflet/dist/images/marker-icon.png";
import markerShadow from "leaflet/dist/images/marker-shadow.png";
import type {
    TrackerOperatorLiveResponse,
    TrackerRouteItem,
    TrackerTask,
    TrackerRouteStatus,
    TrackerOperatorState
} from "@shared/tracker-types";
import { CheckCircle, Clock, Camera, X, Trash2, Package, AlertTriangle } from "lucide-react";
import { FormatStatusTable } from "@/components/shared/FormatStatusTable";
import { TrackerMapLegend } from "./TrackerMapLegend";
import { formatTrackerVisitDateTime } from "./dateFormatting";

delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: markerIcon2x,
    iconUrl: markerIcon,
    shadowUrl: markerShadow
});

const DEFAULT_MAP_CENTER: [number, number] = [53.35, -6.26];
const POLL_INTERVAL_ACTIVE = 15000;
const POLL_INTERVAL_BACKGROUND = 60000;

function FitBounds({ points }: { points: [number, number][] }) {
    const map = useMap();
    useEffect(() => {
        if (!points.length) return;
        const bounds = L.latLngBounds(points);
        map.fitBounds(bounds, { padding: [40, 40] });
    }, [map, points]);
    return null;
}

function getStatusColor(status: TrackerRouteStatus): string {
    switch (status) {
        case "DONE": return "#22c55e";      // Green - Complete
        case "PARTIAL": return "#f59e0b";   // Amber - Partial delivery
        case "ACTIVE": return "#3b82f6";    // Blue - In Progress
        case "PENDING": return "#ef4444";   // Red - To Do
        case "ISSUE": return "#f97316";     // Orange - Issue
        case "ORPHAN": return "#a855f7";    // Purple - Orphan
        default: return "#6b7280";
    }
}

function getTaskStatusBadge(status: TrackerTask['status']) {
    switch (status) {
        case 'complete':
            return <Badge variant="success" className="text-xs">Complete</Badge>;
        case 'partial':
            return <Badge variant="warning" className="text-xs">Partial</Badge>;
        case 'issue':
            return <Badge variant="destructive" className="text-xs">Issue</Badge>;
        case 'pending':
        default:
            return <Badge variant="outline" className="text-xs">Pending</Badge>;
    }
}

function TaskCard({ task, onPhotoClick }: { task: TrackerTask; onPhotoClick: (url: string) => void }) {
    const actionWord = task.taskType === 'REMOVAL' ? 'Removed' : 'Delivered';
    const hasFormatBreakdown = task.mediaFormats && task.mediaFormats.length > 0;

    return (
        <div className="border rounded-lg p-3 space-y-2">
            {/* Header: Campaign name + Assignment ID + Status */}
            <div className="flex items-center justify-between">
                <div className="font-medium text-sm flex items-center gap-2">
                    {task.taskType === 'REMOVAL' ? (
                        <Trash2 className="h-3.5 w-3.5 text-orange-500" />
                    ) : (
                        <Package className="h-3.5 w-3.5 text-blue-500" />
                    )}
                    {task.campaignName}
                    {task.assignmentId && (
                        <span className="text-xs text-muted-foreground font-normal">
                            #{task.assignmentId}
                        </span>
                    )}
                </div>
                <div className="flex items-center gap-1.5">
                    {task.taskType === 'REMOVAL' && (
                        task.basisUsed === 'DELIVERED'
                            ? <Badge variant="outline" className="text-xs bg-violet-50 text-violet-700 border-violet-200">Confirmed delivery</Badge>
                            : <Badge variant="outline" className="text-xs text-muted-foreground">Planned</Badge>
                    )}
                    {getTaskStatusBadge(task.status)}
                </div>
            </div>

            {/* Pack name if present */}
            {task.packName && (
                <div className="text-xs text-muted-foreground">
                    Pack: {task.packName}
                </div>
            )}

            {/* Format-level breakdown - using shared component */}
            {hasFormatBreakdown ? (
                <FormatStatusTable
                    formats={task.mediaFormats}
                    deliveredLabel={actionWord}
                    compact
                />
            ) : (
                /* Fallback: Simple planned/delivered if no format breakdown */
                <FormatStatusTable
                    formats={[{
                        formatId: task.taskId,
                        formatName: task.packName || task.campaignName,
                        planned: task.expectedQuantity,
                        delivered: task.completedQuantity
                    }]}
                    deliveredLabel={actionWord}
                    showTotal={false}
                    compact
                />
            )}

            {/* Photo count */}
            {task.photoCount > 0 && (
                <div className="flex items-center gap-1 text-xs text-muted-foreground">
                    <Camera className="h-3 w-3" />
                    {task.photoCount} photo{task.photoCount !== 1 ? "s" : ""}
                </div>
            )}

            <TaskSubmissionNotes installerNote={task.installerNote} notes={task.notes} />

            {/* Photo thumbnails */}
            {task.photos.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                    {task.photos.map(photo => (
                        <button
                            key={photo.photoId}
                            type="button"
                            onClick={() => onPhotoClick(photo.fullUrl)}
                            className="w-16 h-16 rounded border overflow-hidden hover:ring-2 ring-primary transition-all"
                        >
                            <img
                                src={photo.thumbUrl}
                                alt="Evidence"
                                className="w-full h-full object-cover"
                                onError={(e) => {
                                    (e.target as HTMLImageElement).src = photo.fullUrl;
                                }}
                            />
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}

export default function OperatorRouteView() {
    const params = useParams<{ operatorId: string }>();
    const searchParams = new URLSearchParams(window.location.search);
    const cycleId = searchParams.get("cycleId");
    const retailerId = searchParams.get("retailerId");
    const taskDefinitionIds = searchParams.get("taskDefinitionIds");
    const operatorId = params.operatorId ? parseInt(params.operatorId) : null;

    const [selectedStore, setSelectedStore] = useState<TrackerRouteItem | null>(null);
    const [lightboxPhoto, setLightboxPhoto] = useState<string | null>(null);
    const detailPanelRef = React.useRef<HTMLDivElement>(null);

    const [isTabVisible, setIsTabVisible] = useState(!document.hidden);
    useEffect(() => {
        const handleVisibility = () => setIsTabVisible(!document.hidden);
        document.addEventListener("visibilitychange", handleVisibility);
        return () => document.removeEventListener("visibilitychange", handleVisibility);
    }, []);

    const pollInterval = isTabVisible ? POLL_INTERVAL_ACTIVE : POLL_INTERVAL_BACKGROUND;

    const { data, isLoading, isRefetching, refetch, dataUpdatedAt } = useQuery<TrackerOperatorLiveResponse | null>({
        queryKey: ["tracker-operator-live", cycleId, operatorId, taskDefinitionIds],
        queryFn: async () => {
            if (!cycleId || !operatorId) return null;
            return await getJson<TrackerOperatorLiveResponse>(
                `/api/analytics/tracker/operator/${cycleId}/${operatorId}/live${trackerApiQueryString(taskDefinitionIds)}`,
            );
        },
        enabled: !!cycleId && !!operatorId,
        refetchInterval: pollInterval,
        staleTime: 10000
    });

    // Build query string preserving both retailerId and cycleId
    const cycleQueryObj = new URLSearchParams();
    if (retailerId) cycleQueryObj.set("retailerId", retailerId);
    if (cycleId) cycleQueryObj.set("cycleId", cycleId);
    copyTaskDefinitionIdsToParams(cycleQueryObj, window.location.search);
    const cycleQuery = cycleQueryObj.toString() ? `?${cycleQueryObj.toString()}` : "";

    const getStateBadge = (state: TrackerOperatorState) => {
        switch (state) {
            case "ACTIVE": return <Badge className="bg-green-500 hover:bg-green-600">Active</Badge>;
            case "IDLE": return <Badge variant="outline" className="text-orange-500 border-orange-200 bg-orange-50">Idle</Badge>;
            case "OFFLINE": return <Badge variant="outline" className="text-gray-400">Offline</Badge>;
        }
    };

    const getStatusBadge = (status: TrackerRouteStatus) => {
        switch (status) {
            case "DONE": return <Badge variant="secondary" className="bg-green-100 text-green-700">Done</Badge>;
            case "PARTIAL": return <Badge variant="warning" className="text-xs">Partial</Badge>;
            case "ACTIVE": return <Badge className="bg-blue-500 hover:bg-blue-600">Active</Badge>;
            case "PENDING": return <Badge variant="outline" className="text-gray-400">Pending</Badge>;
            case "ISSUE": return <Badge variant="destructive">Issue</Badge>;
            case "ORPHAN": return <Badge variant="outline" className="text-purple-500 border-purple-200 bg-purple-50">Orphan</Badge>;
            default: return <Badge variant="outline">Unknown</Badge>;
        }
    };

    const getLastSyncLabel = (isoDate: string | null) => {
        if (!isoDate) return "Never";
        try {
            return formatDistanceToNow(new Date(isoDate), { addSuffix: true });
        } catch {
            return "Invalid date";
        }
    };

    const orderedRoute = useMemo(() => {
        if (!data?.routeItems) return [];
        return [...data.routeItems].sort((a, b) => {
            if (a.sequence !== null && b.sequence !== null) return a.sequence - b.sequence;
            const tsA = a.checkInAt ? new Date(a.checkInAt).getTime() : 0;
            const tsB = b.checkInAt ? new Date(b.checkInAt).getTime() : 0;
            return tsA - tsB;
        });
    }, [data?.routeItems]);

    const routePoints = useMemo(() => {
        return orderedRoute
            .filter(item => typeof item.lat === "number" && typeof item.lng === "number" && !isNaN(item.lat) && !isNaN(item.lng))
            .map(item => [item.lat as number, item.lng as number] as [number, number]);
    }, [orderedRoute]);

    const handleStoreClick = (item: TrackerRouteItem) => {
        setSelectedStore(item);
        // Scroll detail panel into view on mobile/smaller screens
        setTimeout(() => {
            detailPanelRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 100);
    };

    // Stats
    const totalStores = orderedRoute.length;
    const completedStores = orderedRoute.filter(r => r.status === "DONE" || r.status === "PARTIAL").length;
    const totalPostings = orderedRoute.reduce((sum, r) => sum + r.postings.length, 0);
    const totalRemovals = orderedRoute.reduce((sum, r) => sum + (r.removals?.length || 0), 0);
    // Count tasks marked done by operator (complete = full delivery, partial = short delivery)
    const completedPostings = orderedRoute.reduce((sum, r) =>
        sum + r.postings.filter(p => p.status === 'complete' || p.status === 'partial').length, 0);
    const partialPostings = orderedRoute.reduce((sum, r) =>
        sum + r.postings.filter(p => p.status === 'partial').length, 0);
    const completedRemovals = orderedRoute.reduce((sum, r) =>
        sum + (r.removals?.filter(p => p.status === 'complete' || p.status === 'partial').length || 0), 0);
    const partialRemovals = orderedRoute.reduce((sum, r) =>
        sum + (r.removals?.filter(p => p.status === 'partial').length || 0), 0);
    const totalPhotos = orderedRoute.reduce((sum, r) =>
        sum + r.postings.reduce((s, p) => s + p.photoCount, 0) + (r.removals?.reduce((s, p) => s + p.photoCount, 0) || 0), 0);

    return (
        <TrackerLayout
            title={data?.operator.operatorName || "Operator Route"}
            lastUpdated={dataUpdatedAt ? new Date(dataUpdatedAt).toISOString() : null}
            onRefresh={() => refetch()}
            isRefreshing={isRefetching}
        >
            {!cycleId || !operatorId ? (
                <div className="flex h-full items-center justify-center text-muted-foreground">
                    Invalid cycle or operator
                </div>
            ) : isLoading && !data ? (
                <div className="flex h-full items-center justify-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                </div>
            ) : data ? (
                <div className="space-y-6 max-w-7xl mx-auto">
                    {/* Header */}
                    <div className="flex items-center justify-between">
                        <div className="space-y-1">
                            <div className="text-2xl font-bold">{data.operator.operatorName}</div>
                            <div className="text-sm text-muted-foreground">
                                Last sync {getLastSyncLabel(data.operator.lastSyncAt)}
                            </div>
                        </div>
                        <div className="flex items-center gap-3">
                            {getStateBadge(data.operator.state)}
                            <Link href={`/admin/activation-cockpit/progress/operators${cycleQuery}`}>
                                <Button variant="outline" size="sm">All Operators</Button>
                            </Link>
                            <Link href={`/admin/activation-cockpit/dashboard${cycleQuery}`}>
                                <Button variant="ghost" size="sm">Dashboard</Button>
                            </Link>
                        </div>
                    </div>

                    {/* Summary Stats */}
                    <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
                        <Card>
                            <CardContent className="pt-4 pb-3">
                                <div className="text-xl font-bold">{completedStores}/{totalStores}</div>
                                <p className="text-xs text-muted-foreground">Stores Done</p>
                            </CardContent>
                        </Card>
                        <Card>
                            <CardContent className="pt-4 pb-3">
                                <div className="text-xl font-bold text-blue-600">{completedPostings}/{totalPostings}</div>
                                <p className="text-xs text-muted-foreground">Postings</p>
                                {partialPostings > 0 && (
                                    <p className="text-xs text-orange-500">{partialPostings} partial</p>
                                )}
                            </CardContent>
                        </Card>
                        {totalRemovals > 0 && (
                            <Card>
                                <CardContent className="pt-4 pb-3">
                                    <div className="text-xl font-bold text-orange-600">{completedRemovals}/{totalRemovals}</div>
                                    <p className="text-xs text-muted-foreground">Removals</p>
                                    {partialRemovals > 0 && (
                                        <p className="text-xs text-orange-500">{partialRemovals} partial</p>
                                    )}
                                </CardContent>
                            </Card>
                        )}
                        <Card>
                            <CardContent className="pt-4 pb-3">
                                <div className="text-xl font-bold">{totalPhotos}</div>
                                <p className="text-xs text-muted-foreground">Photos</p>
                            </CardContent>
                        </Card>
                        <Card>
                            <CardContent className="pt-4 pb-3">
                                <div className="text-xl font-bold text-red-500">
                                    {orderedRoute.filter(r => r.issueCount > 0).length}
                                </div>
                                <p className="text-xs text-muted-foreground">Issues</p>
                            </CardContent>
                        </Card>
                        <Card>
                            <CardContent className="pt-4 pb-3">
                                <div className="text-xl font-bold">
                                    {orderedRoute.filter(r => r.status === "PENDING").length}
                                </div>
                                <p className="text-xs text-muted-foreground">Pending</p>
                            </CardContent>
                        </Card>
                    </div>

                    {/* Map + Store List Side by Side */}
                    <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                        {/* Map */}
                        <Card>
                            <CardHeader className="pb-2">
                                <CardTitle className="text-base">Route Map</CardTitle>
                                {routePoints.length === 0 && orderedRoute.length > 0 && (
                                    <p className="text-xs text-orange-600 mt-1">
                                        Store coordinates not available - {orderedRoute.length} store(s) cannot be shown on map
                                    </p>
                                )}
                                <div className="pt-2">
                                    <TrackerMapLegend />
                                </div>
                            </CardHeader>
                            <CardContent>
                                {routePoints.length === 0 ? (
                                    <div className="h-[350px] w-full flex items-center justify-center bg-gray-50 rounded-lg border">
                                        <div className="text-center text-muted-foreground p-4">
                                            <div className="text-4xl mb-2">📍</div>
                                            <p className="font-medium">No map coordinates available</p>
                                            <p className="text-sm mt-1">
                                                The {orderedRoute.length} store(s) on this route don't have latitude/longitude data.
                                            </p>
                                            <p className="text-xs mt-2 text-gray-400">
                                                Store addresses need to be geocoded to display on map.
                                            </p>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="h-[350px] w-full overflow-hidden rounded-lg border">
                                        <MapContainer
                                            center={routePoints[0] || DEFAULT_MAP_CENTER}
                                            zoom={12}
                                            className="h-full w-full"
                                            scrollWheelZoom
                                        >
                                            <TileLayer
                                                attribution="&copy; OpenStreetMap contributors"
                                                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                                            />
                                            {routePoints.length > 0 && (
                                                <>
                                                    <FitBounds points={routePoints} />
                                                    <Polyline positions={routePoints} color="#2563eb" weight={3} opacity={0.6} />
                                                </>
                                            )}
                                            {orderedRoute.map(item => {
                                                if (typeof item.lat !== "number" || typeof item.lng !== "number" || isNaN(item.lat) || isNaN(item.lng)) return null;
                                                return (
                                                    <CircleMarker
                                                        key={item.routeItemId}
                                                        center={[item.lat, item.lng]}
                                                        radius={10}
                                                        fillColor={getStatusColor(item.status)}
                                                        fillOpacity={0.9}
                                                        color={selectedStore?.routeItemId === item.routeItemId ? "#000" : "#fff"}
                                                        weight={selectedStore?.routeItemId === item.routeItemId ? 3 : 2}
                                                        eventHandlers={{ click: () => handleStoreClick(item) }}
                                                    >
                                                        <Popup>
                                                            <div className="space-y-1 min-w-[180px]">
                                                                <div className="font-semibold">{item.storeName}</div>
                                                                <div className="text-xs text-gray-500">{item.storeAddress}</div>
                                                                <div className="flex items-center gap-2 mt-1">
                                                                    {getStatusBadge(item.status)}
                                                                    <span className="text-xs">{item.postings.length} campaign(s)</span>
                                                                </div>
                                                                <Button
                                                                    size="sm"
                                                                    variant="outline"
                                                                    className="w-full mt-2"
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        e.preventDefault();
                                                                        handleStoreClick(item);
                                                                    }}
                                                                >
                                                                    View Details
                                                                </Button>
                                                            </div>
                                                        </Popup>
                                                    </CircleMarker>
                                                );
                                            })}
                                        </MapContainer>
                                    </div>
                                )}
                            </CardContent>
                        </Card>

                        {/* Store List */}
                        <Card>
                            <CardHeader className="pb-2">
                                <CardTitle className="text-base">Stores ({orderedRoute.length})</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="space-y-2 max-h-[350px] overflow-auto">
                                    {orderedRoute.map(item => (
                                        <button
                                            key={item.routeItemId}
                                            type="button"
                                            onClick={() => handleStoreClick(item)}
                                            className={`w-full text-left rounded-lg border px-4 py-3 hover:bg-muted transition-colors ${selectedStore?.routeItemId === item.routeItemId ? "ring-2 ring-primary bg-muted" : ""
                                                }`}
                                        >
                                            <div className="flex items-center justify-between gap-2">
                                                <div className="flex items-center gap-3">
                                                    <span
                                                        className="w-3 h-3 rounded-full flex-shrink-0"
                                                        style={{ backgroundColor: getStatusColor(item.status) }}
                                                    />
                                                    <div>
                                                        <div className="font-medium">{item.storeName}</div>
                                                        <div className="text-xs text-muted-foreground">
                                                            {item.postings.length > 0 && (() => {
                                                                const done = item.postings.filter(p => p.status === 'complete' || p.status === 'partial').length;
                                                                const full = item.postings.filter(p => p.status === 'complete').length;
                                                                const partial = item.postings.filter(p => p.status === 'partial').length;
                                                                return (
                                                                    <span className="text-blue-600">
                                                                        {done}/{item.postings.length} postings
                                                                        {partial > 0 && <span className="text-orange-500"> ({partial} partial)</span>}
                                                                    </span>
                                                                );
                                                            })()}
                                                            {item.postings.length > 0 && item.removals && item.removals.length > 0 && " • "}
                                                            {item.removals && item.removals.length > 0 && (() => {
                                                                const done = item.removals.filter(p => p.status === 'complete' || p.status === 'partial').length;
                                                                const full = item.removals.filter(p => p.status === 'complete').length;
                                                                const partial = item.removals.filter(p => p.status === 'partial').length;
                                                                return (
                                                                    <span className="text-orange-600">
                                                                        {done}/{item.removals.length} removals
                                                                        {partial > 0 && <span className="text-orange-500"> ({partial} partial)</span>}
                                                                    </span>
                                                                );
                                                            })()}
                                                            {" • "}
                                                            {item.postings.reduce((s, p) => s + p.photoCount, 0) + (item.removals?.reduce((s, p) => s + p.photoCount, 0) || 0)} photos
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    {item.issueCount > 0 && (
                                                        <span className="text-xs text-orange-500 flex items-center gap-1">
                                                            <AlertTriangle className="h-3 w-3" />
                                                            {item.issueCount}
                                                        </span>
                                                    )}
                                                    {item.status === "DONE" && (
                                                        <CheckCircle className="h-4 w-4 text-green-500" />
                                                    )}
                                                    {item.status === "ACTIVE" && (
                                                        <Clock className="h-4 w-4 text-blue-500" />
                                                    )}
                                                    {getStatusBadge(item.status)}
                                                </div>
                                            </div>
                                        </button>
                                    ))}
                                    {orderedRoute.length === 0 && (
                                        <div className="text-sm text-muted-foreground text-center py-8">
                                            No stores in this operator's route.
                                        </div>
                                    )}
                                </div>
                            </CardContent>
                        </Card>
                    </div>

                    {/* Store Detail Panel (Postings/Removals) - Below */}
                    <Card ref={detailPanelRef}>
                        <CardHeader className="pb-2">
                            <CardTitle className="text-base">
                                {selectedStore ? selectedStore.storeName : "Select a store"}
                            </CardTitle>
                            {selectedStore && (
                                <div className="flex items-center gap-3 mt-1">
                                    {getStatusBadge(selectedStore.status)}
                                    <span className="text-sm text-muted-foreground">{selectedStore.storeAddress}</span>
                                    <div className="text-xs text-muted-foreground ml-auto">
                                        {selectedStore.checkInAt && (
                                            <span>In: {formatTrackerVisitDateTime(selectedStore.checkInAt)}</span>
                                        )}
                                        {selectedStore.checkOutAt && (
                                            <span className="ml-2">Out: {formatTrackerVisitDateTime(selectedStore.checkOutAt)}</span>
                                        )}
                                    </div>
                                </div>
                            )}
                        </CardHeader>
                        <CardContent>
                            {!selectedStore ? (
                                <div className="text-center text-muted-foreground py-8">
                                    Click a marker on the map or a store in the list to see postings and removals
                                </div>
                            ) : (
                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                    {/* Postings */}
                                    <div className="space-y-3">
                                        <div className="text-sm font-medium flex items-center gap-2 border-b pb-2">
                                            <Package className="h-4 w-4 text-blue-500" />
                                            Postings ({selectedStore.postings.length})
                                        </div>
                                        {selectedStore.postings.length > 0 ? (
                                            <div className="space-y-3 max-h-[400px] overflow-auto">
                                                {selectedStore.postings.map((task, idx) => (
                                                    <TaskCard key={idx} task={task} onPhotoClick={setLightboxPhoto} />
                                                ))}
                                            </div>
                                        ) : (
                                            <div className="text-sm text-muted-foreground italic py-4">No postings</div>
                                        )}
                                    </div>

                                    {/* Removals */}
                                    <div className="space-y-3">
                                        <div className="text-sm font-medium flex items-center gap-2 border-b pb-2">
                                            <Trash2 className="h-4 w-4 text-orange-500" />
                                            Removals ({selectedStore.removals?.length || 0})
                                        </div>
                                        {selectedStore.removals && selectedStore.removals.length > 0 ? (
                                            <div className="space-y-3 max-h-[400px] overflow-auto">
                                                {selectedStore.removals.map((task, idx) => (
                                                    <TaskCard key={idx} task={task} onPhotoClick={setLightboxPhoto} />
                                                ))}
                                            </div>
                                        ) : (
                                            <div className="text-sm text-muted-foreground italic py-4">No removals</div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </div>
            ) : (
                <div className="flex h-full items-center justify-center text-muted-foreground">
                    No route data found for this operator.
                </div>
            )}

            {/* Lightbox - z-[1000] to appear above Leaflet maps (which use z-index 400-500) */}
            {lightboxPhoto && (
                <div
                    className="fixed inset-0 z-[1000] bg-black/90 flex items-center justify-center p-4"
                    onClick={() => setLightboxPhoto(null)}
                >
                    <button
                        type="button"
                        className="absolute top-4 right-4 text-white hover:text-gray-300 p-2"
                        onClick={() => setLightboxPhoto(null)}
                        title="Close lightbox"
                        aria-label="Close lightbox"
                    >
                        <X className="h-8 w-8" />
                    </button>
                    <img
                        src={lightboxPhoto}
                        alt="Evidence full size"
                        className="max-w-full max-h-full object-contain rounded-lg"
                        onClick={(e) => e.stopPropagation()}
                    />
                </div>
            )}
        </TrackerLayout>
    );
}
